import React, { useCallback, useState } from 'react';
import { Container, Graphics } from '@pixi/react';

const Triangle = ({ shape, isSelected, onUpdate, onSelect, isBlueprintMode = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);

  const handleMouseDown = useCallback((event) => {
    if (!shape) return;
    
    event.stopPropagation();
    onSelect();
    
    const globalPoint = event.data?.global || event;
    setIsDragging(true);
    setDragStart({ 
      x: globalPoint.x - shape.x, 
      y: globalPoint.y - shape.y 
    });
  }, [shape, onSelect]);

  const handleMouseMove = useCallback((event) => {
    if (!isDragging || !dragStart) return;
    
    const globalPoint = event.data?.global || event;
    const newX = globalPoint.x - dragStart.x;
    const newY = globalPoint.y - dragStart.y;
    
    onUpdate({ x: newX, y: newY });
  }, [isDragging, dragStart, onUpdate]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  const drawTriangle = useCallback((g) => {
    if (!shape) return;
    
    g.clear();
    
    // Use shape alpha for preview mode, default to 0.8 for normal mode
    const fillAlpha = shape.isPreview ? (shape.alpha || 0.5) : 0.8;
    const lineAlpha = shape.isPreview ? (shape.alpha || 0.5) : 1;
    
    // Draw main triangle
    const strokeColor = isBlueprintMode ? 0xffffff : (isSelected ? 0xff6b6b : 0x333333);
    const fillColor = isBlueprintMode ? 0xffffff : (shape.color || 0xcc9900);
    g.lineStyle(isSelected ? 3 : 1, strokeColor, lineAlpha);
    g.beginFill(fillColor, fillAlpha);
    
    const { width, height } = shape;
    
    // Draw triangle
    g.moveTo(width / 2, 0); // Top point
    g.lineTo(width, height); // Bottom right
    g.lineTo(0, height); // Bottom left
    g.closePath();
    g.endFill();
  }, [shape, isSelected]);

  const drawNodes = useCallback((g) => {
    if (!shape || !isSelected) return;
    
    g.clear();
    
    const { width, height } = shape;
    
    // Draw center node
    g.beginFill(0x4ecdc4);
    g.drawCircle(width / 2, height / 2, 8);
    g.endFill();
    
    // Draw vertex nodes
    const vertexPositions = [
      { x: width / 2, y: 0 },     // top
      { x: 0, y: height },        // bottom left
      { x: width, y: height }     // bottom right
    ];
    
    vertexPositions.forEach((pos) => {
      g.beginFill(0xff6b6b);
      g.drawCircle(pos.x, pos.y, 6);
      g.endFill();
    });
  }, [shape, isSelected]);

  if (!shape) return null;

  return (
    <Container 
      x={shape.x} 
      y={shape.y}
      eventMode="static"
      onpointerdown={handleMouseDown}
      onpointermove={handleMouseMove}
      onpointerup={handleMouseUp}
    >
      {/* Main triangle */}
      <Graphics draw={drawTriangle} />
      
      {/* Control nodes */}
      {isSelected && (
        <Graphics draw={drawNodes} />
      )}
    </Container>
  );
};

export default Triangle; 